import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { projectsAPI, getImageUrl } from '../utils/api';
import '../styles/Projects.scss';

const PROGRAM_MAP = {
  'Software-Project': { label: 'Software Projects', domains: ['B.Tech', 'Diploma', 'BCA', 'MCA', 'M.Tech'] },
  'B.Tech': { label: 'BE / BTech Projects', domains: ['B.Tech'] },
  'Diploma': { label: 'Diploma Projects', domains: ['Diploma'] },
  'BCA-MCA': { label: 'BCA / MCA Projects', domains: ['BCA', 'MCA'] },
  'BCA/MCA': { label: 'BCA / MCA Projects', domains: ['BCA', 'MCA'] },
};

const pageSize = 12;

function ProgramProjects() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const programParam = searchParams.get('program') || '';
  const programConfig = PROGRAM_MAP[programParam];
  const programLabel = programConfig?.label || 'Projects';

  const [projects, setProjects] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeDomain, setActiveDomain] = useState('All');
  const [domainGroup, setDomainGroup] = useState([]);
  
  

  useEffect(() => {
    if (programConfig?.domains?.length === 1) {
      setActiveDomain(programConfig.domains[0]);
      setDomainGroup([]);
    } else if (programConfig?.domains?.length > 1) {
      setActiveDomain('All');
      setDomainGroup(programConfig.domains);
    } else {
      setActiveDomain('All');
      setDomainGroup([]);
    }
  }, [programParam, programConfig?.domains]);
  

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (activeDomain !== 'All') {
      params.domain = activeDomain;
    }

    projectsAPI.getPublic(params)
      .then(response => {
        const data = response.data;
        let projectsArray = data?.data || data || [];
        const validProjects = Array.isArray(projectsArray) ? projectsArray.map(p => {
          let parsedImages = [];
          try {
            if (p.images) {
              parsedImages = typeof p.images === 'string' ? JSON.parse(p.images) : p.images;
            }
          } catch (e) {
            console.error('Error parsing images for project', p.id, e);
          }

          return {
            ...p,
            images: parsedImages,
            tech_stack: p.tech_stack || p.technologies || 'Technology not specified'
          };
        }) : [];
        setProjects(validProjects);
        setFiltered(validProjects);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Fetch error:', err);
        setProjects([]);
        setFiltered([]);
        setLoading(false);
      });
  }, [activeDomain]);

  useEffect(() => {
    let result = [...projects];
    if (activeCategory !== 'All') result = result.filter(p => p.category === activeCategory);
    if (domainGroup.length > 0) {
      result = result.filter(p => domainGroup.includes(p.domain));
    } else if (activeDomain !== 'All') {
      result = result.filter(p => p.domain === activeDomain);
    }
    if (search) result = result.filter(p =>
      (p.title?.toLowerCase() || '').includes(search.toLowerCase()) ||
      (p.description_1?.toLowerCase() || '').includes(search.toLowerCase()) ||
      (p.category?.toLowerCase() || '').includes(search.toLowerCase())
    );
    result.sort((a, b) => b.id - a.id);
    setFiltered(result);
    setCurrentPage(1);
  }, [activeCategory, activeDomain, search, projects, domainGroup]);

  const clearFilters = () => {
    setActiveCategory('All');
    setSearch('');
  };

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageStart = (currentPage - 1) * pageSize;
  const paginatedProjects = filtered.slice(pageStart, pageStart + pageSize);

  const activeStats = useMemo(() => {
    const total = filtered.length;
    const freeCount = filtered.filter(p => p.price === 0 || p.price === null).length;
    return { total, freeCount };
  }, [filtered]);

  return (
    <div className="projects-page">
      <div className="projects-body">
        <section className="program-header">
          <div>
            <Link to="/projects" className="program-back">← Back to Programs</Link>
            <h2>{programLabel}</h2>
            <p>Explore curated projects with filters tailored for this program.</p>
          </div>
          <div className="program-stats">
            <div>
              <span>Total</span>
              <strong>{activeStats.total}</strong>
            </div>
            <div>
              <span>Free</span>
              <strong>{activeStats.freeCount}</strong>
            </div>
          </div>
        </section>

        <section className="projects-results">
          <div className="filters-panel">
            <div className="filter-group">
              <div className="filter-item">
                <label>🔎 Search</label>
                <div className="search-input-group">
                  <input
                    className="category-search"
                    type="text"
                    placeholder="Search by title, technology..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                  {search && (
                    <button className="clear-search" onClick={() => setSearch('')}>✕</button>
                  )}
                </div>
              </div>
              <div className="filter-item">
                <label>📂 Category</label>
                <select className="filter-select" value={activeCategory} onChange={(e) => setActiveCategory(e.target.value)}>
                  <option value="All">All Categories</option>
                  {[...new Set(projects.map(p => p.category).filter(Boolean))].map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="filter-item clear-item">
                <button className="btn-clear-all" onClick={clearFilters}>🧹 Clear Filters</button>
              </div>
            </div>
          </div>

          <main className="projects-grid-area">
            {loading ? (
              <div className="loading-grid">
                {[1, 2, 3, 4, 5, 6, 7, 8].map(i => <div key={i} className="skeleton-card" />)}
              </div>
            ) : filtered.length === 0 ? (
              <div className="no-results">
                <span>😕</span>
                <p>No projects found matching your criteria.</p>
                <button onClick={clearFilters} className="btn-clear-empty">Clear All Filters</button>
              </div>
            ) : (
              <div className="projects-grid">
                {paginatedProjects.map(project => (
                  <ProgramProjectCard key={project.id} project={project} onView={() => navigate(`/premium-preview/${project.id}?source=software`)} />
                ))}
              </div>
            )}
            {!loading && filtered.length > 0 && (
              <div className="projects-pagination">
                <span>Showing {filtered.length === 0 ? 0 : pageStart + 1}-{Math.min(pageStart + pageSize, filtered.length)} of {filtered.length}</span>
                <div className="pagination-actions">
                  <button
                    className="page-btn"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    Prev
                  </button>
                  <span className="page-indicator">{currentPage} / {totalPages}</span>
                  <button
                    className="page-btn"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </main>
        </section>
      </div>
      
    </div>
  );
}

function ProgramProjectCard({ project, onView }) {
  const isFree = !project.price || project.price === 0;



  const desc = project.description_1 || project.description || project.about;
  const displayDescription = desc ? desc.split(' ').slice(0, 10).join(' ') + '...' : 'No description available...';

  return (
    <div className="project-card">
      <div className="card-thumbnail">
        {project.images && project.images.length > 0 ? (
          <img
            src={getImageUrl(project.images[0])}
            alt={project.title}
            className="thumbnail-img"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className="thumb-placeholder" style={{ display: project.images && project.images.length > 0 ? 'none' : 'flex' }}>
          <span>{(project.category || 'P')?.charAt(0)}</span>
        </div>
      </div>
      <div className="card-body">
        <div className="card-category">{project.category || 'Uncategorized'}</div>
        <h3 className="card-title">{project.title || 'Untitled'}</h3>
        <p className="card-desc">{displayDescription}</p>
      </div>

      <div className="card-techstack">
        <div className="tech-label">⚙️ Tech Stack</div>
        <div className="tech-list">
          {project.tech_stack &&
            (Array.isArray(project.tech_stack) ? project.tech_stack : String(project.tech_stack).split(',')).slice(0, 4).map((tech, i) => (
              <span key={i} className="tech-tag">{tech.trim()}</span>
            ))}
        </div>
      </div>
        <div className="card-actions">
          <button className="btn-details" onClick={() => onView && onView(project)}>🔎 VIEW MORE</button>
          <Link to={`/premium-preview/${project.id}?source=software&enquiry=true`} className="btn-enquire">💬 Enquire</Link>
        </div>
    </div>
  );
}

export default ProgramProjects;
